import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { WeatherMoon24Regular, WeatherSunny24Regular } from "@fluentui/react-icons";

import { useExampleLanguage } from "../../contexts/ExampleLanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useVoiceAutoSend } from "../../contexts/VoiceAutoSendContext";
import { profileMenuMaxHeight, readProfileMenuLimits } from "../../utils/profileMenuHeight";
import { isSpeechInputSupported } from "../../utils/speechSupport";
import styles from "./ProfileButton.module.css";

export interface ProfileDetails {
    username?: string | null;
    displayName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    storeId?: string | null;
    storeName?: string | null;
    roles?: string[];
}

interface ProfileButtonProps {
    profile: ProfileDetails;
    buildId: string;
    onLogout?: () => void;
    compact?: boolean;
    /** ENG-032: links into the admin platform, by role. The app passes it; the widget never does. */
    showPlatformLinks?: boolean;
}

const getInitials = (profile: ProfileDetails): string => {
    const firstInitial = profile.firstName?.trim()?.[0] || "";
    const lastInitial = profile.lastName?.trim()?.[0] || "";
    if (firstInitial || lastInitial) {
        return `${firstInitial}${lastInitial}`.toUpperCase();
    }

    const source = (profile.displayName || profile.username || profile.email || "U").trim();
    if (!source) return "U";
    if (source.includes(",")) {
        const [lastRaw, firstRaw] = source.split(",", 2).map(value => value.trim());
        const combined = `${firstRaw?.[0] || ""}${lastRaw?.[0] || ""}`.toUpperCase();
        if (combined) return combined;
    }

    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
    }
    return (
        source
            .replace(/[^\p{L}\p{N}]/gu, "")
            .slice(0, 2)
            .toUpperCase() || "U"
    );
};

const getRoleBadgeColor = (role: string): string => {
    switch (role) {
        case "Admin":
            return "#b91c1c";
        case "User":
            return "#1d4ed8";
        case "Analytics":
            return "#047857";
        default:
            return "#374151";
    }
};

export const ProfileButton = ({ profile, buildId, onLogout, compact = false, showPlatformLinks = false }: ProfileButtonProps) => {
    const { theme, toggleTheme } = useTheme();
    const { exampleLanguage, toggleExampleLanguage } = useExampleLanguage();
    const { enabled: voiceAutoSend, toggle: toggleVoiceAutoSend, available: voiceAvailable, hostControlled } = useVoiceAutoSend();
    // ENG-014: only meaningful where the mic button itself can appear — supported by the browser
    // and (ENG-015) not turned off by the admin for this channel.
    // ENG-034: hidden when the host fixed autoSend via the widget's init option — there is
    // nothing for the employee to choose (VoiceAutoSendContext: hostControlled). Not gated on
    // the widget's speechEnabled option: with the mic hidden but no autoSend override,
    // ChatWidget.submitText({inputMode:"voice"}) still needs this preference.
    const showVoiceAutoSend = isSpeechInputSupported() && voiceAvailable && !hostControlled;
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const popupRef = useRef<HTMLDivElement | null>(null);
    const [menuMaxHeight, setMenuMaxHeight] = useState<number | null>(null);

    // ENG-057: size the menu to the space that is really below it, not to `vh` (0 in a short
    // WebView). Before paint when it opens, and again whenever the window or the keyboard resizes.
    useLayoutEffect(() => {
        const popup = popupRef.current;
        if (!menuOpen || !popup) return;
        const fit = () => setMenuMaxHeight(profileMenuMaxHeight(popup.getBoundingClientRect().top, readProfileMenuLimits(popup)));
        fit();
        window.addEventListener("resize", fit);
        window.visualViewport?.addEventListener("resize", fit);
        return () => {
            window.removeEventListener("resize", fit);
            window.visualViewport?.removeEventListener("resize", fit);
        };
    }, [menuOpen]);

    useEffect(() => {
        const onPointerDown = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, []);

    const isDark = theme === "dark";
    const isGreek = exampleLanguage === "el";
    const fullName = [profile.firstName, profile.lastName]
        .map(value => value?.trim())
        .filter(Boolean)
        .join(" ");
    const displayName = fullName || profile.displayName?.trim() || profile.username?.trim() || profile.email?.trim() || "User";
    const initials = getInitials(profile);
    const roles = profile.roles || [];
    const hasStoreDetails = Boolean(profile.storeName || profile.storeId);
    const canAdmin = showPlatformLinks && roles.includes("Admin");
    const canAnalytics = showPlatformLinks && (roles.includes("Admin") || roles.includes("Analytics"));

    return (
        <div ref={menuRef} className={styles.menuWrapper}>
            <button
                type="button"
                onClick={() => setMenuOpen(open => !open)}
                aria-label="Open user menu"
                title={displayName}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className={`${styles.avatarButton} ${compact ? styles.avatarButtonCompact : ""}`}
            >
                {initials}
            </button>

            {menuOpen && (
                <div
                    ref={popupRef}
                    role="menu"
                    aria-label="User menu"
                    className={styles.menu}
                    style={menuMaxHeight === null ? undefined : { maxHeight: menuMaxHeight }}
                >
                    <div className={styles.identitySection}>
                        <div className={styles.displayName}>{displayName}</div>
                        {profile.email && <div className={styles.secondaryText}>{profile.email}</div>}
                    </div>

                    {hasStoreDetails && (
                        <div className={styles.detailsSection}>
                            {profile.storeName && (
                                <div className={styles.detailRow}>
                                    <span>{isGreek ? "Κατάστημα" : "Store"}</span>
                                    <strong>{profile.storeName}</strong>
                                </div>
                            )}
                            {profile.storeId && (
                                <div className={styles.detailRow}>
                                    <span>{isGreek ? "ID καταστήματος" : "Store ID"}</span>
                                    <strong>{profile.storeId}</strong>
                                </div>
                            )}
                        </div>
                    )}

                    {!hasStoreDetails && roles.length > 0 && (
                        <div className={styles.rolesSection}>
                            <div className={styles.sectionLabel}>Roles</div>
                            <div className={styles.roles}>
                                {roles.map(role => (
                                    <span
                                        key={role}
                                        className={styles.roleBadge}
                                        style={{ borderColor: getRoleBadgeColor(role), color: getRoleBadgeColor(role) }}
                                    >
                                        {role}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className={styles.settingSection}>
                        <span className={styles.settingLabel}>Theme</span>
                        <button type="button" onClick={toggleTheme} aria-label={`Switch to ${isDark ? "light" : "dark"} theme`} className={styles.themeToggle}>
                            <span className={`${styles.toggleTrack} ${!isDark ? styles.toggleTrackActive : ""}`}>
                                <span className={`${styles.toggleThumb} ${!isDark ? styles.toggleThumbLight : ""}`}>
                                    {isDark ? <WeatherMoon24Regular /> : <WeatherSunny24Regular />}
                                </span>
                            </span>
                        </button>
                    </div>

                    <div className={styles.settingSection}>
                        <span className={styles.settingLabel}>Examples language</span>
                        <button
                            type="button"
                            onClick={toggleExampleLanguage}
                            aria-label={`Switch examples language to ${isGreek ? "EN" : "ΕΛ"}`}
                            className={styles.languageToggle}
                        >
                            <span className={`${styles.languageOption} ${isGreek ? styles.languageOptionActive : ""}`}>ΕΛ</span>
                            <span className={`${styles.languageOption} ${!isGreek ? styles.languageOptionActive : ""}`}>EN</span>
                        </button>
                    </div>

                    {showVoiceAutoSend && (
                        <div className={styles.settingSection}>
                            <span className={styles.settingLabel}>{isGreek ? "Αυτόματη αποστολή μετά την εκφώνηση" : "Send automatically after speaking"}</span>
                            <button
                                type="button"
                                onClick={toggleVoiceAutoSend}
                                aria-label={isGreek ? "Αυτόματη αποστολή μετά την εκφώνηση" : "Send automatically after speaking"}
                                aria-pressed={voiceAutoSend}
                                className={styles.languageToggle}
                            >
                                <span className={`${styles.languageOption} ${voiceAutoSend ? styles.languageOptionActive : ""}`}>{isGreek ? "Ναι" : "On"}</span>
                                <span className={`${styles.languageOption} ${!voiceAutoSend ? styles.languageOptionActive : ""}`}>
                                    {isGreek ? "Όχι" : "Off"}
                                </span>
                            </button>
                        </div>
                    )}

                    {(canAdmin || canAnalytics) && (
                        <div className={styles.linksSection}>
                            {canAdmin && (
                                <a href="#/admin" className={styles.menuLink} onClick={() => setMenuOpen(false)}>
                                    Admin
                                </a>
                            )}
                            {canAnalytics && (
                                <a href="#/admin/analytics/overview" className={styles.menuLink} onClick={() => setMenuOpen(false)}>
                                    Analytics
                                </a>
                            )}
                        </div>
                    )}

                    <div className={styles.buildSection}>
                        <span>Build ID</span>
                        <code>{buildId || "dev"}</code>
                    </div>

                    {onLogout && (
                        <div className={styles.logoutSection}>
                            <button
                                type="button"
                                onClick={() => {
                                    setMenuOpen(false);
                                    onLogout();
                                }}
                                className={styles.logoutButton}
                            >
                                Log Out
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
